import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, Save, CheckCircle } from 'lucide-react';
import { performanceService } from '../../services/performanceService';
import { useAuth } from '../../context/AuthContext';
import StatusBadge from '../../components/StatusBadge';

const RATINGS = ['Poor', 'Average', 'Good', 'Excellent'];

const PerformanceDetail = () => {
  const { reviewId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [ratings, setRatings] = useState({}); // KPI ratings state

  const isHR = user?.role === 'super_admin' || user?.role === 'hr_manager';
  const isOwnReview = review?.employee_id === (user?.employee_id || user?.user_id);

  useEffect(() => {
    loadReview();
  }, [reviewId]);

  const loadReview = async () => {
    try {
      const res = await performanceService.getReviewDetails(reviewId);
      setReview(res.data);
      // Initialize ratings state from existing data
      const initialRatings = {};
      res.data.kpis.forEach(k => {
        initialRatings[k.kpi_name] = isHR ? (k.manager_rating || '') : (k.self_rating || '');
      });
      setRatings(initialRatings);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRatingChange = (kpiName, val) => {
    setRatings(prev => ({ ...prev, [kpiName]: val }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        kpis: review.kpis.map(k => ({
          kpi_name: k.kpi_name,
          [isHR ? 'manager_rating' : 'self_rating']: ratings[k.kpi_name]
        })),
        [isHR ? 'manager_comments' : 'self_comments']: e.target.overall_comments.value,
        ...(isHR && { final_rating: e.target.final_rating.value, feeds_into_increment: e.target.increment.checked })
      };

      if (isHR) {
        await performanceService.submitManagerAssessment(reviewId, payload);
      } else {
        await performanceService.submitSelfAssessment(reviewId, payload);
      }
      navigate('/performance');
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="flex justify-center p-20"><Loader2 className="animate-spin" /></div>;
  if (!review) return <div className="p-10 text-center text-red-500">Review not found</div>;

  const canEdit = (review.status === 'Self-Assessment Pending' && isOwnReview) || 
                  (review.status === 'Manager Review Pending' && isHR);

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-20">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-500 hover:text-gray-900 text-sm">
        <ArrowLeft className="w-4 h-4" /> Back to List
      </button>

      <div className="bg-white rounded-card border shadow-soft p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{review.employee_name}</h1>
            <p className="text-gray-500">{review.cycle} Review &middot; {review.period_start} to {review.period_end}</p>
          </div>
          <StatusBadge status={review.status} />
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* KPI Rating Section */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold border-b pb-2">KPI Assessment</h2>
            {review.kpis.map((kpi, idx) => (
              <div key={idx} className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{kpi.kpi_name}</p>
                    <p className="text-sm text-gray-500">{kpi.description}</p>
                    {review.status !== 'Self-Assessment Pending' && kpi.self_rating && (
                      <p className="text-xs mt-2 text-primary">Self Rating: <b>{kpi.self_rating}</b></p>
                    )}
                  </div>
                  
                  {canEdit ? (
                    <div className="flex gap-2">
                      {RATINGS.map(r => (
                        <button
                          key={r} type="button"
                          onClick={() => handleRatingChange(kpi.kpi_name, r)}
                          className={`px-3 py-1.5 rounded text-xs font-medium border transition-colors ${ratings[kpi.kpi_name] === r ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 hover:border-primary'}`}
                        >
                          {r}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-right">
                      <p className="text-xs text-gray-400 uppercase font-bold">Manager Rating</p>
                      <p className="font-bold text-primary">{kpi.manager_rating || 'N/A'}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Comments Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold mb-2">Self Comments</label>
              <textarea 
                name="overall_comments"
                disabled={!canEdit || isHR}
                defaultValue={review.self_comments || ''}
                placeholder="Employee's remarks..."
                className="w-full p-3 border rounded-lg text-sm h-32 disabled:bg-gray-50"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2">Manager Comments</label>
              <textarea 
                name={isHR ? "overall_comments" : "manager_comments"}
                disabled={!isHR || review.status === 'Completed'}
                defaultValue={review.manager_comments || ''}
                placeholder="Manager's feedback..."
                className="w-full p-3 border rounded-lg text-sm h-32 disabled:bg-gray-50"
              />
            </div>
          </div>

          {/* Final Approval Section (HR Only) */}
          {isHR && review.status === 'Manager Review Pending' && (
            <div className="bg-primary/5 p-6 rounded-xl border border-primary/10 space-y-4">
              <h3 className="font-bold text-primary">Final Appraisal Decision</h3>
              <div className="flex flex-wrap items-center gap-6">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Final Overall Rating</label>
                  <select name="final_rating" required className="p-2 border rounded text-sm bg-white min-w-[150px]">
                    {RATINGS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-2 mt-4">
                  <input type="checkbox" name="increment" id="increment" className="w-4 h-4 accent-primary" />
                  <label htmlFor="increment" className="text-sm text-gray-700">Recommend for Increment / Bonus</label>
                </div>
              </div>
            </div>
          )}

          {canEdit && (
            <div className="flex justify-end pt-6 border-t">
              <button 
                type="submit" disabled={submitting}
                className="bg-primary text-white px-8 py-3 rounded-control font-semibold flex items-center gap-2 shadow-lg shadow-primary/20 hover:scale-[1.02] transition-transform"
              >
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
                Submit Assessment
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default PerformanceDetail;